import React, { useState, useEffect } from "react";
import { View, StyleSheet, Animated } from "react-native";
import {
  CodeField,
  Cursor,
  useBlurOnFulfill,
  useClearByFocusCell,
} from "react-native-confirmation-code-field";
import { useTheme } from "../context/ThemeContext";

/**
 * Per-digit OTP / security-code field — same component shape as
 * ntvoiplib/VoIPLib/voiplib-rn's NtCodeInput so the verification UX matches
 * across NTConnect products. Built on `react-native-confirmation-code-field`
 * which already handles the per-cell focus, paste-fill, and cursor concerns.
 *
 * Props:
 *   - length   Number of digits (default 6).
 *   - value    Controlled value. When omitted, the component is uncontrolled.
 *   - onChange Fires on every keystroke with the current value.
 *   - onComplete Fires once the user has entered `length` digits.
 *   - autoFocus Focus the first empty cell on mount (web only).
 */
function NtCodeInput({ length = 6, value, onChange, onComplete, autoFocus = false }) {
  const { theme } = useTheme();
  const [internal, setInternal] = useState(value || "");

  // Keep internal state in sync with the controlled `value` so the parent can
  // clear/reset the field (e.g. after a wrong-code error).
  useEffect(() => {
    if (value !== undefined && value !== internal) setInternal(value);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [value]);

  const ref = useBlurOnFulfill({ value: internal, cellCount: length });
  const [props, getCellOnLayoutHandler] = useClearByFocusCell({
    value: internal,
    setValue: setInternal,
  });

  const handleChangeText = (text) => {
    // Sanitize — strip anything that isn't a digit so paste of "  123 456"
    // still lands as "123456".
    const cleaned = (text || "").replace(/\D/g, "").slice(0, length);
    setInternal(cleaned);
    if (onChange) onChange(cleaned);
    if (cleaned.length === length && onComplete) onComplete(cleaned);
  };

  return (
    <View style={styles.container}>
      <CodeField
        ref={ref}
        {...props}
        value={internal}
        onChangeText={handleChangeText}
        cellCount={length}
        rootStyle={styles.codeFieldRoot}
        keyboardType="number-pad"
        textContentType="oneTimeCode"
        autoComplete="sms-otp"
        autoFocus={autoFocus}
        renderCell={({ index, symbol, isFocused }) => (
          <Animated.Text
            key={index}
            style={[
              styles.cell,
              {
                backgroundColor: symbol ? theme.pillBg : theme.surface,
                borderColor: symbol
                  ? theme.brand2
                  : isFocused
                  ? theme.brand2
                  : theme.borderSoft,
                borderWidth: isFocused ? 2 : 1.5,
                color: theme.text,
              },
            ]}
            onLayout={getCellOnLayoutHandler(index)}
          >
            {symbol || (isFocused ? <Cursor /> : null)}
          </Animated.Text>
        )}
      />
    </View>
  );
}

const styles = StyleSheet.create({
  container: {},
  codeFieldRoot: {
    justifyContent: "flex-start",
    gap: 10,
  },
  cell: {
    width: 52,
    height: 60,
    lineHeight: 56,
    fontSize: 24,
    fontWeight: "700",
    textAlign: "center",
    borderRadius: 12,
    overflow: "hidden",
  },
});

export default NtCodeInput;
